import React, {useEffect, useRef, useState} from "react";
import "../DesignerRenderer.css";
import {getLiveTagByName, getLiveTagValueByName, isStaleAnalogTag} from "../utils";

const TagRenderer = ({
                         object,
                         latestTags,
                         styleJson,
                         dataJson,
                         commonStyle,
                         handleMouseDown,
                         onButtonAction,
                         renderSelectionHandles,
                     }) => {
    const initializedAnalogRef = useRef(false);
    const [analogInputValue, setAnalogInputValue] = useState("");
    const [isAnalogDirty, setIsAnalogDirty] = useState(false);

    const tagMode = dataJson.tag_mode || "manual";
    const manuallyOff = tagMode === "off";
    const usesTagValue = tagMode === "realtime" || manuallyOff;
    const objectMode = dataJson.object_mode || "monitor";
    const controlType = dataJson.control_type || "digital";
    const realtimeTagName = dataJson.tag_name || "";

    const prefix = dataJson.prefix || "";
    const suffix = dataJson.suffix || "";
    const unit = dataJson.unit || "";

    const liveTag = getLiveTagByName(latestTags, realtimeTagName);
    const liveValue = getLiveTagValueByName(latestTags, realtimeTagName);
    const missingRealtimeTag = usesTagValue && Boolean(realtimeTagName) && !liveTag;
    const lastValueRef = useRef({objectId: object.id, tagName: realtimeTagName, value: ""});
    if (lastValueRef.current.objectId !== object.id || lastValueRef.current.tagName !== realtimeTagName) {
        lastValueRef.current = {objectId: object.id, tagName: realtimeTagName, value: ""};
    }
    if (liveValue !== "") {
        lastValueRef.current.value = liveValue;
    }
    // Missing/archived resources are intentionally omitted by the live API.
    // Keep only a value already received for this exact binding, never the
    // object's manual/default value, and label the unavailable binding UNKNOWN.
    const displayedLiveValue = missingRealtimeTag ? lastValueRef.current.value : liveValue;
    const connectionStatus = String(
        liveTag?.connection_status || (missingRealtimeTag ? "UNKNOWN" : "")
    ).trim().toUpperCase();
    // Monitoring objects keep the default control_type even when the linked
    // PLC tag is analog. Signal metadata is authoritative for stale styling.
    const staleTag = manuallyOff || (tagMode === "realtime" && (
        missingRealtimeTag || isStaleAnalogTag(liveTag?.signal_type || controlType, connectionStatus)
    ));

    useEffect(() => {
        initializedAnalogRef.current = false;
        setIsAnalogDirty(false);
        setAnalogInputValue("");
    }, [object.id, dataJson.tag_name, dataJson.control_type, dataJson.object_mode]);

    useEffect(() => {
        if (objectMode !== "control" || controlType !== "analog") return;
        if (initializedAnalogRef.current) return;

        setAnalogInputValue(liveValue);
        initializedAnalogRef.current = true;
    }, [objectMode, controlType, realtimeTagName, liveValue]);

    let resolvedValue = dataJson.value ?? "N/A";

    if (usesTagValue && realtimeTagName) {
        resolvedValue = displayedLiveValue !== "" ? displayedLiveValue : "N/A";
    }

    const selectedHandles = renderSelectionHandles();

    const handleLocalMouseDown = (e) => {
        e.stopPropagation();
        handleMouseDown?.(e);
    };

    // Both Designer and Runtime use this path. Confirm before invoking the
    // action callback so cancellation cannot reach either page's write API.
    const handleWrite = (value) => {
        if (staleTag || manuallyOff || object.locked || objectMode !== "control" ||
            typeof onButtonAction !== "function") return;

        if (dataJson.confirm_before_write === true) {
            const customMessage = typeof dataJson.write_confirmation_message === "string"
                ? dataJson.write_confirmation_message.trim()
                : "";
            const requestedValue = controlType === "digital" ? (value === 1 ? "ON" : "OFF") : String(value);
            const message = customMessage ||
                `Are you sure you want to set "${realtimeTagName || object.name || "this tag"}" to ${requestedValue}?`;
            if (!window.confirm(message)) return;
        }

        onButtonAction({
            ...object,
            __controlType: controlType,
            __controlValue: value,
        });
    };

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleLocalMouseDown}
            className={`dr-object dr-tag${staleTag ? " dr-tag--stale" : ""}`}
            aria-label={manuallyOff ? "Sensor off" : undefined}
            title={manuallyOff ? "Sensor off" : undefined}
            style={{
                position: "absolute",
                ...commonStyle,
                fontSize: styleJson.fontSize || 16,
                fontWeight: styleJson.fontWeight || "normal",
                fontFamily: styleJson.fontFamily || "Arial",

                // 👇 کنترل بکگراند
                background: staleTag
                    ? "#f4dce2"
                    : styleJson.hasBackground
                        ? styleJson.backgroundColor || "#e0f2fe"
                        : "transparent",

                border: staleTag ? "1px solid rgba(136, 19, 55, 0.42)" : undefined,
                boxShadow: staleTag
                    ? "0 0 0 1px rgba(136, 19, 55, 0.08), 0 0 8px rgba(136, 19, 55, 0.14)"
                    : undefined,

                borderRadius: styleJson.borderRadius || 0,
                padding: styleJson.hasBackground ? styleJson.padding || 4 : 0,

                outline: selectedHandles ? "2px solid #2563eb" : "none",
                outlineOffset: selectedHandles ? "2px" : "0",
            }}
        >
            <div className="dr-tag-content">
                {manuallyOff ? prefix : <>
                    {prefix}
                    {String(resolvedValue)}
                    {unit}
                    {suffix}
                    {connectionStatus && connectionStatus !== "ONLINE" ? ` ${connectionStatus}` : ""}
                </>}
            </div>

            {!manuallyOff && objectMode === "control" && !object.locked && (
                <div
                    className="dr-control-group"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    {controlType === "digital" ? (
                        <>
                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--green"
                                disabled={staleTag}
                                onClick={() => handleWrite(1)}
                            >
                                ON
                            </button>

                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--red"
                                disabled={staleTag}
                                onClick={() => handleWrite(0)}
                            >
                                OFF
                            </button>
                        </>
                    ) : (
                        <>
                            <input
                                type="number"
                                step="any"
                                value={analogInputValue}
                                disabled={staleTag}
                                aria-label="Analog write value"
                                onChange={(e) => {
                                    setAnalogInputValue(e.target.value);
                                    setIsAnalogDirty(true);
                                }}
                                placeholder={liveValue || "live value"}
                                className="dr-control-input"
                            />

                            <button
                                type="button"
                                className="dr-control-btn dr-control-btn--blue"
                                disabled={staleTag}
                                onClick={() => {
                                    const finalValue =
                                        analogInputValue === "" ? liveValue : analogInputValue;
                                    handleWrite(finalValue);
                                }}
                            >
                                Write
                            </button>

                            {(staleTag || (!isAnalogDirty && liveValue)) && (
                                <span className={staleTag ? "dr-stale-badge" : "dr-live-badge"}>
                                    {staleTag ? "stale" : "live"}
                                </span>
                            )}
                        </>
                    )}
                </div>
            )}

            {selectedHandles}
        </div>
    );
};

export default TagRenderer;
